import React, { useState, useRef, useEffect, useMemo } from "react";
import { newsData as newsDefaults, NewsItem } from "../../data/newsData";
import { useCmsSection } from "../../cms/useCmsSection";
import { applyNewsPatch } from "../../cms/apply";
import fallbackImg from "../../assets/MainPlaygroundView.webp";

function normalizeHref(href?: string): string {
  if (!href) return "#";
  return /^https?:\/\//i.test(href) ? href : `https://${href}`;
}

function safeParseDate(s: string): number {
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
}

export function NewsSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const { data, loading, error } = useCmsSection<readonly NewsItem[]>(
    "news",
    newsDefaults,
    applyNewsPatch
  );

  const content = data ?? newsDefaults;

  // newest first (handles unparseable dates gracefully)
  const sorted = useMemo(
    () => [...content].sort((a, b) => safeParseDate(b.date) - safeParseDate(a.date)),
    [content]
  );

  // keep index in range if data changes
  useEffect(() => {
    setActiveSlide((prev) => (sorted.length ? Math.min(prev, sorted.length - 1) : 0));
  }, [sorted.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null || sorted.length === 0) return;
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) < minSwipeDistance) return;
    setActiveSlide((prev) =>
      distance > 0
        ? (prev < sorted.length - 1 ? prev + 1 : 0)
        : (prev > 0 ? prev - 1 : sorted.length - 1)
    );
  };

  if (content.length === 0) return <div />;

  return (
    <section className="py-12 bg-transparent">
      <div className="text-center mb-8 px-4">
        <h2 className="text-3xl font-semibold text-slate-700">Our Latest News and Insights</h2>
        {loading && <p className="text-sm text-gray-500 mt-2">Loading…</p>}
        {error && <p className="text-sm text-red-600 mt-2">Failed to load news.</p>}
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 max-w-6xl mx-auto">
        {sorted.map((article) => (
          <article key={article.id} className="bg-white rounded-lg overflow-hidden shadow flex flex-col">
            <img
              src={article.image || (fallbackImg as unknown as string)}
              alt={article.imageAlt || article.title}
              className="w-full h-48 object-cover"
              loading="lazy"
            />
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="text-xl font-semibold text-slate-700 mb-2">{article.title}</h3>
              <time className="text-sm text-zinc-600 mb-4">{article.date}</time>
              <p className="text-sm text-zinc-600 flex-1">{article.excerpt}</p>
              {article.link && article.link.length > 1 && (
                <a
                  href={normalizeHref(article.link)}
                  className="mt-4 inline-block text-sm font-medium text-slate-700 hover:text-teal-600"
                >
                  Read more →
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* Mobile Carousel */}
      <div
        className="md:hidden px-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {sorted.length > 0 && (
          <>
            <article className="bg-white rounded-lg overflow-hidden shadow mx-auto max-w-sm">
              <img
                src={
                  sorted[activeSlide].image ||
                  (fallbackImg as unknown as string)
                }
                alt={sorted[activeSlide].imageAlt || sorted[activeSlide].title}
                className="w-full h-48 object-cover"
                loading="lazy"
                width={600}
                height={400}
              />
              <div className="p-4">
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  {sorted[activeSlide].title}
                </h3>
                <time className="text-sm text-zinc-600 mb-4 block">
                  {sorted[activeSlide].date}
                </time>
                <p className="text-sm text-zinc-600 mb-4">{sorted[activeSlide].excerpt}</p>
                {sorted[activeSlide].link && sorted[activeSlide].link.length > 1 && (
                  <a
                    href={normalizeHref(sorted[activeSlide].link)}
                    className="inline-block text-sm font-medium text-slate-700 hover:text-teal-600"
                  >
                    Read more →
                  </a>
                )}
              </div>
            </article>

            {/* Dots */}
            <div className="flex justify-center mt-4" role="tablist" aria-label="News slides">
              {sorted.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`w-3 h-3 mx-1 rounded-full transition-colors ${
                    idx === activeSlide ? "bg-teal-600" : "bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                  aria-selected={idx === activeSlide}
                  role="tab"
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Read More Articles Button */}
      <div className="text-center mt-8">
        <a
          href="/news"
          className="inline-block px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition"
        >
          Read More Articles
        </a>
      </div>
    </section>
  );
}
